import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { handleError } from '@library/utilities/helpers/handleError'
import { OfferDetails, OfferMerchantActions, OfferPageParent, OfferStatus, type TagProp } from './constants'
import { message } from 'gokwik-ui-kit'

export function getStatusVariant(status: string): TagProp['variant'] {
    switch (status) {
        case OfferStatus.DRAFT:
            return 'warning'

        case OfferStatus.ACTIVE:
        case OfferStatus.PUBLISHED:
            return 'success'

        case OfferStatus.EXPIRED:
            return 'error'

        default:
            return 'default'
    }
}

export function validateStatus(status: string) {
    if (Object.values(OfferStatus).includes(status as OfferStatus)) return status
    return OfferStatus.ACTIVE
}

const statusText = (status: string) => {
    switch (status) {
        case OfferStatus.DRAFT:
            return 'Offer not yet published'

        case OfferStatus.ACTIVE:
            return 'Offer published and active'

        case OfferStatus.AVAILABLE:
            return 'Offer available'

        case OfferStatus.INACTIVE:
            return 'Offer published but inactive'

        case OfferStatus.PUBLISHED:
            return 'Offer published'

        case OfferStatus.EXPIRED:
            return 'Offer expired'

        default:
            return null
    }
}

export function getStatusTooltip(status: string, adminAsMerchant: boolean): string {
    return statusText(status) + (adminAsMerchant ? ' for this merchant' : '')
}

export const getOfferListing = async (params, callback: CallableFunction, parent?: OfferPageParent) => {
    try {
        params = Object.keys(params).reduce((prev, curr) => {
            if (![null, undefined, ''].includes(params[curr])) prev[curr] = params[curr]
            return prev
        }, {})
        let response = await makeAPICall({
            method: 'get',
            url:
                process.env.REACT_APP_AUX_BASE_URL +
                (parent === OfferPageParent.PAYMENT
                    ? APIEndPoints.offers.merchantOffers
                    : APIEndPoints.offers.getAllOffers),
            params,
        })
        if (response?.data?.data?.documents?.length > 0) {
            let docs = response?.data?.data?.documents ?? []
            if (parent === OfferPageParent.PAYMENT) {
                docs = docs.map((row) => {
                    return { ...row, ...row.offer }
                })
            }
            callback(docs, response.data.data.count ?? 0)
        } else {
            callback([], 0)
            !response?.success && handleError(response)
        }
    } catch (error) {
        callback([], 0)
        handleError(error)
    }
}

export const getDraftOfferListing = async (params, callback: CallableFunction) => {
    try {
        params = Object.keys(params).reduce((prev, curr) => {
            if (![null, undefined, ''].includes(params[curr])) prev[curr] = params[curr]
            return prev
        }, {})
        let response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.getAllDraftOffers,
            params,
        })
        if (response?.data?.data?.documents?.length > 0) {
            callback(response?.data?.data?.documents ?? [], response.data.data.count ?? 0)
        } else {
            callback([], 0)
            !response?.success && handleError(response)
        }
    } catch (error) {
        callback([], 0)
        handleError(error)
    }
}

export const getOfferDetails = async (id: string, callback: CallableFunction, errorHandle?: CallableFunction) => {
    try {
        let response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.offerDetails(id),
        })
        if (response?.success) {
            if (response?.data?.data) {
                response.data.data.parsedRuleObj = parseRuleObj(response?.data?.data)
            }
            callback(response.data.data)
        } else {
            callback(null)
            errorHandle?.()
            handleError(response)
        }
    } catch (error) {
        callback(null)
        errorHandle?.()
        handleError(error)
    }
}

export const addRemoveMerchants = async (action: OfferMerchantActions, payload, errorCallback) => {
    try {
        const endpoint = APIEndPoints.offers[action]
        let response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_AUX_BASE_URL + endpoint,
            payload,
        })

        if (!response?.success) {
            errorCallback()
            handleError(response)
        } else {
            message.success('Merchant list updated successfully')
        }
    } catch (err) {
        errorCallback()
        handleError(err)
    }
}

export const updateOffer = async (options: {
    id: string
    payload
    offerCode: string
    success?: CallableFunction
    errorCallback?: CallableFunction
}) => {
    try {
        let response = await makeAPICall({
            method: 'patch',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.offerDetails(options.id),
            payload: options.payload,
        })

        if (response?.success) {
            options.success?.()
            message.success(`Offer: ${options.offerCode} updated successfully`)
        } else {
            options.errorCallback?.()
            handleError(response)
        }
    } catch (err) {
        options.errorCallback?.()
        handleError(err)
    }
}

export const updateMerchantOfferConfig = async (options: {
    payload
    offerCode: string
    success?: CallableFunction
    errorCallback?: CallableFunction
}) => {
    try {
        let response = await makeAPICall({
            method: 'patch',
            url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.offers.merchantOffers,
            payload: options.payload,
        })

        if (response?.success) {
            options.success?.()
            message.success(`Offer: ${options.offerCode} updated successfully`)
        } else {
            options.errorCallback?.()
            handleError(response)
        }
    } catch (err) {
        options.errorCallback?.()
        handleError(err)
    }
}

export const parseRuleObj = (data: OfferDetails) => {
    const details = {
        discount_type: undefined,
        payment_method_type: undefined,
        offer_title: undefined,
        amount: undefined,
        percentage: undefined,
        max_discount_amount: undefined,
        limit_discount_amount: false,
        min_cart: 0,
        max_cart: undefined,
        cards: undefined,
        emis: undefined,
        bins: undefined,
        wallets: undefined,
        psp_apps: undefined,
    }

    if (!data) return details

    details.amount = data.rule_details?.actions?.match(/^.*output\.setDiscountAmount\((\d*\.?\d*)\).*$/)?.[1]
    details.amount =
        data.rule_details?.actions?.match(/^.*output\.setCashbackAmount\((\d*\.?\d*)\).*$/)?.[1] || details.amount
    details.amount = isNaN(details.amount) ? details.amount : +details.amount
    details.percentage = data.rule_details?.actions?.match(/^.*output\.setDiscountPercentage\((\d*\.?\d*)\).*$/)?.[1]
    details.percentage =
        data.rule_details?.actions?.match(/^.*output\.setCashbackPercentage\((\d*\.?\d*)\).*$/)?.[1] ||
        details.percentage
    details.percentage = isNaN(details.percentage) ? details.percentage : +details.percentage
    details.discount_type = !!details.percentage ? 'percentage' : 'fixed'
    details.max_discount_amount = data.rule_details?.actions?.match(
        /^.*output\.setMaxDiscountAmount\((\d*\.?\d*)\).*$/,
    )?.[1]
    details.max_discount_amount =
        data.rule_details?.actions?.match(/^.*output\.setMaxCashbackAmount\((\d*\.?\d*)\).*$/)?.[1] ||
        details.max_discount_amount
    details.max_discount_amount = isNaN(details.max_discount_amount)
        ? details.max_discount_amount
        : +details.max_discount_amount
    if (details.max_discount_amount) {
        details.limit_discount_amount = true
    }

    details.offer_title = data.rule_details?.actions
        ?.match(/^.*output\.setOfferTitle\((.+)\).*$/)?.[1]
        ?.replace(/[\"]/g, '')

    details.min_cart = +(data.rule_details?.conditions?.match(/^.*\(input\.cartValue >= (\d*\.?\d*)\).*$/)?.[1] || '')
    details.max_cart = +(data.rule_details?.conditions?.match(/^.*\(input\.cartValue <= (\d*\.?\d*)\).*$/)?.[1] || '') || null

    details.bins = data.rule_details?.conditions?.match(/^.*\[(.*)\]\scontains\sinput\.bin.*$/)?.[1]?.split(/\,\s*/)
    details.psp_apps = data.rule_details?.conditions
        ?.match(/^.*\[(.*)\]\scontains\sinput\.pspApp.*$/)?.[1]
        ?.replace(/[\"]/g, '')
        ?.split(/\,\s*/)?.[0]
    if (details.psp_apps) {
        details.payment_method_type = 'upi'
    }

    if (data.valid_payment_methods?.some((method) => method.startsWith('wallet'))) {
        details.wallets = data.valid_payment_methods[0]
        details.payment_method_type = 'wallet'
    }

    if (data.valid_payment_methods?.some((method) => method.startsWith('emi'))) {
        details.emis = data.valid_payment_methods
        details.payment_method_type = 'emi'
    }

    if (data.valid_payment_methods?.some((method) => ['cc', 'dc'].includes(method))) {
        details.cards = data.valid_payment_methods
        details.payment_method_type = 'cards'
    }

    return details
}
